import { useConnection } from "@solana/wallet-adapter-react";
import { getUnixEpochSeconds } from "@stepfinance/common";
import { useCallback, useEffect, useState } from "react";
import useSWRImmutable from "swr/immutable";

import { isCallOptionClaimed } from "../actions/step/reward-options/utils";
import { useNetworkConfig } from "../contexts/app/config";
import { useEnrichedWallet } from "../contexts/enrichedWallet";
import { useRefreshContext } from "../contexts/refresh";
import { STEP_API_ENDPOINT, swrFetcher } from "../utils/swr";

export type RewardOption = {
  index: number;
  type?: string;
  amount: number;
  rewardMint: string;
  rewardDecimals: number;
  priceMint: string;
  priceDecimals: number;
  strikePrice: number;
  expiry: number;
  claimsBitMaskAccount: string;
  merkleRoot: number[];
  distributorAddress: string;
  priceVault: string;
  rewardVault: string;
  proof: string[];
};

export const useRewardOptions = (): { rewardOptions: RewardOption[]; refreshRewardOptions: () => Promise<void> } => {
  const { connection } = useConnection();
  const { network } = useNetworkConfig();
  const { connected, publicKey } = useEnrichedWallet();

  const { refreshing } = useRefreshContext();

  const { data: rewardContracts, mutate: refresh } = useSWRImmutable<RewardOption[]>(() => {
    if (publicKey) {
      return `${STEP_API_ENDPOINT}/v1/reward-options/contracts?cluster=${network}&pubkey=${publicKey}`;
    }
  }, swrFetcher);
  const [rewardOptionsRefreshCounter, setRewardOptionsRefreshCounter] = useState<number>(0);
  const [filteredRewardOptions, setFilteredRewardOptions] = useState<RewardOption[]>([]);

  const formattedRewardContracts = rewardContracts ?? [];
  const refreshRewardOptions = useCallback(async () => {
    await refresh();
    setRewardOptionsRefreshCounter((counter) => counter + 1);
  }, [refresh]);

  useEffect(() => setFilteredRewardOptions([]), [connected]);

  useEffect(() => {
    if (refreshing) {
      void refreshRewardOptions();
    }
  }, [refreshing, refreshRewardOptions]);

  useEffect(() => {
    void (async () => {
      if (formattedRewardContracts) {
        const formattedContracts = await Promise.all(
          formattedRewardContracts.map(async (option) => {
            const { expiry, index, claimsBitMaskAccount } = option;
            const now = getUnixEpochSeconds();
            const timeRemaining = expiry - now;
            const isClaimed = await isCallOptionClaimed(connection, claimsBitMaskAccount, index);
            if (isClaimed || timeRemaining <= 0) {
              return null;
            }
            return option;
          }),
        );
        const filteredOptions = formattedContracts.filter((c): c is RewardOption => !!c);
        setFilteredRewardOptions(filteredOptions);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connection, formattedRewardContracts.length, rewardOptionsRefreshCounter]);

  return { rewardOptions: filteredRewardOptions, refreshRewardOptions };
};
